import type {
  Product,
  ProductDetails,
  VariableProduct,
} from '@frontend/libs/types/product'
import type React from 'react'

import useCart from '@frontend/libs/hooks/useCart'
import { useCartStore } from '@frontend/libs/stores/useCartStore'
import { useProductStore } from '@frontend/libs/stores/useProductStore'
import { Button } from '@nextui-org/react'
import { __ } from '@wordpress/i18n'
import { useCallback, useEffect, useMemo } from 'react'
import toast from 'react-hot-toast'

import WishlistButton from '../../shop/wishlist/WishlistButton'
import FashionModelPopup from '../components/FashionModelPopup'
import { ProductInfo } from '../components/ProductInfo'
import { ProductMeta } from '../components/ProductMeta'
import SizeGuidePopup from '../components/SizeGuidePopup'
import Swatch, { type SelectedOption } from '../components/swatch/Swatch'

interface ProductSummaryProps {
  onClearSelection: () => void
  onSelectOption: (values: SelectedOption) => void
  product: ProductDetails
  selectedOption: SelectedOption
  showMeta?: boolean
  variation?: Product
}

const ProductSummary: React.FC<ProductSummaryProps> = ({
  onClearSelection,
  onSelectOption,
  product,
  selectedOption,
  showMeta,
  variation,
}) => {
  const {
    id,
    isNewArrival,
    name,
    options,
    price,
    regularPrice,
    salePrice,
    sizeGuideImage,
    sizeText,
    stockStatus,
    url,
  } = product as VariableProduct
  const {
    price: variationPrice,
    regularPrice: variationRegularPrice,
    salePrice: variationSalePrice,
    stockStatus: variationStockStatus,
    url: variationUrl,
  } = variation || {}

  const renderProductOptions = () =>
    options?.map((option) => (
      <Swatch
        key={option.slug}
        onClick={onSelectOption}
        option={option}
        showLabel
        value={selectedOption?.[option.slug]}
      />
    ))

  const { closeQuickView } = useProductStore((state) => state)
  const { openMiniCart, setCart } = useCartStore((state) => state)

  const { addItem, data, error, isLoading } = useCart().add()

  const handleAddToCart = useCallback(async () => {
    const data = {
      id,
      quantity: 1,
      variation: Object.entries(selectedOption).map(([attribute, value]) => ({
        attribute,
        value,
      })),
    }

    await toast
      .promise(addItem(data), {
        error: error?.message || __('Failed to add item to cart', 'asavagroup'),
        loading: __('Adding to cart...', 'asavagroup'),
        success: __('Item added to cart', 'asavagroup'),
      })
      .then((res) => {
        setCart(res)
        closeQuickView()
        openMiniCart()
      })
  }, [
    id,
    selectedOption,
    addItem,
    error?.message,
    setCart,
    closeQuickView,
    openMiniCart,
  ])

  useEffect(() => {
    if (data) {
      setCart(data)
    }
  }, [data, setCart])

  const isInStock = useMemo(() => {
    if (product.type === 'variable') {
      return variation && variationStockStatus === 'instock'
    }
    return stockStatus === 'instock'
  }, [product.type, variation, variationStockStatus, stockStatus])

  const isDisabledButton = useMemo(() => {
    return !selectedOption || !isInStock
  }, [selectedOption, isInStock])

  const getButtonText = () => {
    if (!selectedOption || (product.type === 'variable' && !variation)) {
      return __('Please select an option', 'asavagroup')
    }
    if (!isInStock) {
      return __('Out of stock', 'asavagroup')
    }
    return __('Add to bag', 'asavagroup')
  }

  const renderButtonActions = () => (
    <>
      <Button
        className="w-full"
        color="primary"
        isDisabled={isDisabledButton}
        isLoading={isLoading}
        onClick={handleAddToCart}
        type="button"
        variant="solid"
      >
        {getButtonText()}
      </Button>
      <WishlistButton productId={id} />
    </>
  )

  return (
    <div className="summary entry-summary mb-6 md:sticky md:left-0 md:top-10">
      <div className="hidden md:block">
        <ProductInfo
          isNewArrival={isNewArrival}
          name={name}
          price={variationPrice || price}
          regularPrice={variationRegularPrice || regularPrice}
          salePrice={variationSalePrice || salePrice}
          url={variationUrl || url}
        />
      </div>
      <div className="relative mb-4 mt-6 pb-10">
        <div className="space-y-4">{renderProductOptions()}</div>
        <div className="absolute right-0 top-0 grid gap-2 [&>*]:ml-auto [&>*]:mr-0">
          {sizeText || sizeGuideImage ? (
            <SizeGuidePopup image={sizeGuideImage} text={sizeText} />
          ) : null}
          {product?.fashionModels && (
            <FashionModelPopup fashionModels={product.fashionModels} />
          )}
        </div>
        {selectedOption && (
          <Button
            className="absolute bottom-0 left-0 p-0 underline"
            onClick={onClearSelection}
            size="sm"
            type="button"
            variant="light"
          >
            {__('Clear', 'asavagroup')}
          </Button>
        )}
      </div>
      <div className="hidden space-y-2 md:block">{renderButtonActions()}</div>
      {showMeta && <ProductMeta product={product} />}
      <div className="sticky bottom-0 z-20 -mx-4 block border-t bg-white px-4 py-6 md:hidden">
        <ProductInfo
          isNewArrival={isNewArrival}
          name={name}
          price={variationPrice || price}
          regularPrice={variationRegularPrice || regularPrice}
          salePrice={variationSalePrice || salePrice}
          url={variationUrl || url}
        />
        <div className="mt-6 grid gap-1">{renderButtonActions()}</div>
      </div>
    </div>
  )
}

export default ProductSummary
